import Box from "@mui/material/Box";
import { keyframes } from "@mui/material/styles";
import { useEffect, useId, useRef, useState } from "react";

const LENGTH = 6;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
`;

interface TotpInputProps {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  /** The last code was rejected: red slots; typing a digit starts a new code. */
  error?: boolean;
  /** Read by screen readers; the slots themselves are decoration. */
  label?: string;
  /**
   * The account's email. Rendered as a hidden `username` field so password
   * managers know which saved login's one-time code to offer.
   */
  username?: string;
}

/**
 * A 6-digit authenticator code. One real input under six drawn slots, so
 * paste, password managers, SMS autofill (`one-time-code`) and screen readers
 * all see a single field. Digits only; editing happens at the end.
 *
 * Password managers (1Password) only offer a code for a field they can see
 * and recognise: the input stays rendered (transparent text, not opacity 0 or
 * display none), is named `totp` with `autocomplete="one-time-code"`, and runs
 * 40px past the slots so the manager's inline button lands beside them.
 */
export default function TotpInput({
  value,
  onChange,
  autoFocus,
  disabled,
  error = false,
  label = "Authentication code",
  username,
}: TotpInputProps) {
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  const digits = value.replace(/\D/g, "").slice(0, LENGTH);
  // The slot the next digit goes into; the last one once the code is full.
  const active = Math.min(digits.length, LENGTH - 1);

  const slot = (i: number) => {
    const char = digits[i] ?? "";
    const isActive = focused && !disabled && !error && i === active;
    return (
      <Box key={i} className="slot" data-active={isActive || undefined} data-filled={!!char || undefined} data-error={error || undefined}>
        {char}
        {isActive && !char && <Box className="caret" />}
      </Box>
    );
  };

  return (
    <Box
      onMouseDown={(e) => {
        // Any click lands in the input, with the caret at the end.
        e.preventDefault();
        input.current?.focus();
      }}
      sx={(theme) => {
        const v = (theme.vars ?? theme).palette;
        const ring = (color: string) => `0 0 0 2px ${color}`;
        return {
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          my: 2,
          cursor: disabled ? "default" : "text",
          opacity: disabled ? 0.55 : 1,
          animation: error ? `${shake} 320ms ease` : "none",
          "& .group": { display: "flex", gap: 1 },
          "& .slot": {
            position: "relative",
            display: "grid",
            placeItems: "center",
            width: 44,
            height: 52,
            fontSize: "1.5rem",
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: error ? v.error.main : v.text.primary,
            borderRadius: "8px",
            bgcolor: v.action.hover,
            transition: "box-shadow 150ms, background-color 150ms",
            "&[data-filled]": { bgcolor: v.action.selected },
            "&[data-active]": { bgcolor: v.background.default, boxShadow: ring(v.primary.main) },
            "&[data-error]": { bgcolor: "rgba(var(--mui-palette-error-mainChannel) / 0.12)" },
          },
          "& .caret": {
            width: 2,
            height: 24,
            borderRadius: 1,
            bgcolor: v.primary.main,
            animation: `${blink} 1s step-end infinite`,
          },
          "& .dash": { width: 10, height: 2, borderRadius: 1, bgcolor: v.text.disabled, mx: 0.5 },
          "@media (prefers-reduced-motion: reduce)": { animation: "none", "& .caret": { animation: "none" } },
        };
      }}
    >
      <Box className="group">{[0, 1, 2].map(slot)}</Box>
      <Box className="dash" />
      <Box className="group">{[3, 4, 5].map(slot)}</Box>
      <Box
        component="input"
        ref={input}
        value={digits}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value.replace(/\D/g, "").slice(0, LENGTH))}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          // After a rejected code, the next digit starts over.
          if (error && digits.length === LENGTH && /^\d$/.test(e.key)) {
            e.preventDefault();
            onChange(e.key);
          }
        }}
        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
          // A pasted code replaces whatever is there, even a full one.
          const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LENGTH);
          if (!pasted) return;
          e.preventDefault();
          onChange(pasted);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
          const el = e.currentTarget;
          el.setSelectionRange(el.value.length, el.value.length);
        }}
        disabled={disabled}
        id={id}
        name="totp"
        aria-label={label}
        aria-invalid={error || undefined}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={LENGTH}
        spellCheck={false}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "calc(100% + 40px)",
          clipPath: "inset(0 40px 0 0)",
          border: 0,
          p: 0,
          outline: "none",
          background: "transparent",
          color: "transparent",
          caretColor: "transparent",
          "&::selection": { background: "transparent" },
          fontSize: 16, // no zoom-on-focus on iOS
          cursor: "inherit",
        }}
      />
      {username && (
        // Visually hidden, but present: managers skip display:none fields.
        <Box
          component="input"
          type="text"
          name="username"
          autoComplete="username"
          value={username}
          readOnly
          tabIndex={-1}
          aria-hidden
          sx={{ position: "absolute", width: 1, height: 1, p: 0, m: -1, overflow: "hidden", clipPath: "inset(50%)", border: 0 }}
        />
      )}
    </Box>
  );
}
